import api from './api';
import { Departamento, Usuario } from '../types';

export interface DepartamentoResponse {
  success: boolean;
  departamentos?: Departamento[];
  departamento?: DepartamentoDetalhado;
  message?: string;
}

export interface DepartamentoDetalhado {
  id: string;
  nome: string;
  descricao: string;
  lideres: Usuario[];
  membros: Usuario[];
  estatisticas: {
    total_tarefas: number;
    tarefas_concluidas: number;
    tarefas_ativas: number;
    tarefas_canceladas?: number;
    tarefas_pendentes?: number;
    tarefas_em_andamento?: number;
    tarefas_aguardando_aprovacao?: number;
    tarefas_ajustes_requisitados?: number;
    ultima_atualizacao?: string;
  };
  
  // Campos diretos das estatísticas para compatibilidade
  tarefas_ativas?: number;
  tarefas_concluidas?: number;
  tarefas_canceladas?: number;
  tarefas_pendentes?: number;
  tarefas_em_andamento?: number;
  tarefas_aguardando_aprovacao?: number;
  tarefas_ajustes_requisitados?: number;
  ultima_atualizacao?: string;
}

// Função auxiliar para mapear os dados da API para o formato esperado pelo frontend
const mapearDepartamento = (depApi: any): Departamento => {
  return {
    id: depApi.id,
    nome: depApi.nome,
    descricao: depApi.descricao || '',
    lideres: Array.isArray(depApi.lideres) ? depApi.lideres.map((l: any) => l.id) : [],

    // Estatísticas do departamento (snake_case da API)
    tarefas_ativas: depApi.tarefas_ativas || 0,
    tarefas_concluidas: depApi.tarefas_concluidas || 0,
    tarefas_concluidas_com_atraso: depApi.tarefas_concluidas_com_atraso || 0,
    tarefas_canceladas: depApi.tarefas_canceladas || 0,
    tarefas_pendentes: depApi.tarefas_pendentes || 0,
    tarefas_em_andamento: depApi.tarefas_em_andamento || 0,
    tarefas_aguardando_aprovacao: depApi.tarefas_aguardando_aprovacao || 0,
    ultima_atualizacao: depApi.ultima_atualizacao || null,

    // Versões em camelCase para uso no frontend
    tarefasAtivas: depApi.tarefas_ativas || 0,
    tarefasConcluidas: depApi.tarefas_concluidas || 0,
    tarefasCanceladas: depApi.tarefas_canceladas || 0,
    tarefasPendentes: depApi.tarefas_pendentes || 0,
    tarefasEmAndamento: depApi.tarefas_em_andamento || 0,
    tarefasAguardandoAprovacao: depApi.tarefas_aguardando_aprovacao || 0,
    ultimaAtualizacao: depApi.ultima_atualizacao || null,

    // Objeto tarefas usado pelo DepartamentoGraficos
    tarefas: {
      totalAtivas: depApi.tarefas_ativas || 0,
      pendentes: depApi.tarefas_pendentes || 0,
      aIniciar: depApi.tarefas_a_iniciar || 0,
      emAndamento: depApi.tarefas_em_andamento || 0,
      aguardando: depApi.tarefas_aguardando_aprovacao || 0,
      ajustes: depApi.tarefas_ajustes_requisitados || 0,
      concluidas: depApi.tarefas_concluidas || 0,
      concluidasComAtraso: depApi.tarefas_concluidas_com_atraso || 0,
      emAndamentoComAtraso: depApi.tarefas_em_andamento_com_atraso || 0,
      emAndamentoNoPrazo: depApi.tarefas_em_andamento_no_prazo,
      arquivadas: depApi.tarefas_arquivadas || 0,
      ativasNaoArquivadas: depApi.tarefas_ativas || 0,
    }
  };
};

const mapearDepartamentoDetalhado = (depApi: any): DepartamentoDetalhado => {
  return {
    id: depApi.id,
    nome: depApi.nome,
    descricao: depApi.descricao || '',
    lideres: depApi.lideres?.map((l: any) => ({
      id: l.id,
      nome: l.nome,
      email: l.email,
      cargo: l.cargo,
      departamentoId: depApi.id,
      ehLider: true,
      tipo: 'comum',
      avatar: l.avatar || '',
      dataCriacao: '',
      tarefasAtivas: 0,
      tarefasConcluidas: 0
    })) || [],
    membros: depApi.membros?.map((m: any) => ({
      id: m.id,
      nome: m.nome,
      email: m.email,
      cargo: m.cargo,
      departamentoId: depApi.id,
      ehLider: m.eh_lider || false,
      tipo: 'comum',
      avatar: m.avatar || '',
      dataCriacao: '',
      tarefasAtivas: 0,
      tarefasConcluidas: 0
    })) || [],
    estatisticas: {
      total_tarefas: Number(depApi.estatisticas?.total_tarefas || 0),
      tarefas_concluidas: Number(depApi.estatisticas?.tarefas_concluidas || 0),
      tarefas_ativas: Number(depApi.estatisticas?.tarefas_ativas || 0),
      tarefas_canceladas: Number(depApi.tarefas_canceladas || 0),
      tarefas_pendentes: Number(depApi.tarefas_pendentes || 0),
      tarefas_em_andamento: Number(depApi.tarefas_em_andamento || 0),
      tarefas_aguardando_aprovacao: Number(depApi.tarefas_aguardando_aprovacao || 0),
      ultima_atualizacao: depApi.ultima_atualizacao || null
    },
    
    // Campos diretos das estatísticas para compatibilidade
    tarefas_ativas: Number(depApi.tarefas_ativas || 0),
    tarefas_concluidas: Number(depApi.tarefas_concluidas || 0),
    tarefas_canceladas: Number(depApi.tarefas_canceladas || 0),
    tarefas_pendentes: Number(depApi.tarefas_pendentes || 0),
    tarefas_em_andamento: Number(depApi.tarefas_em_andamento || 0),
    tarefas_aguardando_aprovacao: Number(depApi.tarefas_aguardando_aprovacao || 0),
    ultima_atualizacao: depApi.ultima_atualizacao || null
  };
};

/**
 * Serviço para gerenciar departamentos
 */
export interface LiderancaUsuario {
  id: string;
  nome: string;
  data_atribuicao: string;
}

export interface LiderancasResponse {
  success: boolean;
  liderancas?: LiderancaUsuario[];
  message?: string;
}

export const departamentoService = {
  /**
   * Obter todas as lideranças de um usuário
   * @param usuarioId ID do usuário
   * @returns Promise com a lista de departamentos onde o usuário é líder
   */
  obterLiderancasUsuario: async (usuarioId: string): Promise<LiderancasResponse> => {
    try {
      const response = await api.get<any>(`/usuarios/${usuarioId}/liderancas`);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao obter lideranças do usuário ${usuarioId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter lideranças do usuário'
      };
    }
  },

  /**
   * Obter todos os departamentos
   * @returns Promise com a lista de departamentos
   */
  obterTodos: async (): Promise<DepartamentoResponse> => {
    try {
      const response = await api.get<any>('/departamentos');
      if (response.data.success && response.data.departamentos) {
        // Mapear os departamentos para o formato esperado pelo frontend
        const departamentosMapeados = response.data.departamentos.map(mapearDepartamento);
        return {
          success: true,
          departamentos: departamentosMapeados
        };
      }
      return response.data;
    } catch (error: any) {
      console.error('Erro ao obter departamentos:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter departamentos'
      };
    }
  },

  /**
   * Obter detalhes de um departamento específico
   * @param id ID do departamento
   * @returns Promise com os detalhes do departamento
   */
  obterPorId: async (id: string): Promise<DepartamentoResponse> => {
    try {
      const response = await api.get<any>(`/departamentos/${id}`);
      if (response.data.success && response.data.departamento) {
        // Mapear o departamento detalhado para o formato esperado pelo frontend
        const departamentoDetalhado = mapearDepartamentoDetalhado(response.data.departamento);
        return {
          success: true,
          departamento: departamentoDetalhado
        };
      }
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao obter departamento ${id}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao obter departamento'
      };
    }
  },

  /**
   * Adicionar um líder ao departamento
   * @param departamentoId ID do departamento
   * @param usuarioId ID do usuário
   * @returns Promise com o resultado da operação
   */
  adicionarLider: async (departamentoId: string, usuarioId: string): Promise<DepartamentoResponse> => {
    try {
      const response = await api.post<DepartamentoResponse>(
        `/departamentos/${departamentoId}/lideres`,
        { usuario_id: usuarioId }
      );
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao adicionar líder ao departamento ${departamentoId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao adicionar líder'
      };
    }
  },

  /**
   * Remover um líder do departamento
   * @param departamentoId ID do departamento
   * @param usuarioId ID do usuário
   * @returns Promise com o resultado da operação
   */
  removerLider: async (departamentoId: string, usuarioId: string): Promise<DepartamentoResponse> => {
    try {
      const response = await api.delete<DepartamentoResponse>(
        `/departamentos/${departamentoId}/lideres/${usuarioId}`
      );
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao remover líder do departamento ${departamentoId}:`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover líder'
      };
    }
  }
};

export default departamentoService;
